import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import debounce from "lodash/debounce";
import uniqWith from "lodash/uniqWith";
import React, { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from "react";
import { useAssessmentState } from "../../../../services/assessment-state";
import { fetchJSON } from "../../../../utils/ajax";
import { useClickOutsideEl } from "../../../../utils/hooks";
import {
    SuburbInput,
    Outer,
    Results,
    ResultRow,
    InfoMessage,
    ErrorMessage,
    SearchWrapper,
    CloseButton,
} from "./styled";

export type Location = {
    name: string;
    state: string;
    postcode: number;
    lat: number;
    lng: number;
};

interface Props {
    onLocationSelect: (location: Location | undefined) => void;
}

type SearchResponse = Location[];

const renderLocationLabel = ({ name, state, postcode }: Location): string => {
    return `${name} ${state} ${postcode}`;
};

const useSuburbSearchService = () => {
    // const [selectedLocation, setSelectedLocation] = useState<Location | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | undefined>();
    const [data, setData] = useState<SearchResponse | undefined>();

    const search = useCallback((query: string) => {
        if (query === "") {
            setData(undefined);
            return;
        }
        setError(undefined);
        setLoading(true);
        return fetchJSON<SearchResponse>(`/wp-json/hss/v1/search?q=${query}`)
            .then(newData => {
                setData(
                    uniqWith(newData, (a, b) => renderLocationLabel(a) === renderLocationLabel(b)),
                );
            })
            .catch(error => {
                console.error(error);
                setError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return { data, loading, error, search };
};

export const SelectSuburbSearch: FC<Props> = props => {
    
    const { state, setState } = useAssessmentState();

    const [value, setValue] = useState("");

    const [showResults, setShowResults] = useState(false);
    const { data, loading, error, search } = useSuburbSearchService();
    const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>();
    const debouncedSearch = useMemo(() => debounce(search, 250), [search]);
    const ref = useRef<HTMLDivElement>(null);
    const { onLocationSelect } = props;
    const resultsScrollingRef = useRef<HTMLDivElement>(null);

    useClickOutsideEl(ref, () => {
        setFocusedOptionIndex(null);
        setShowResults(false);
    });

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputval = event.target.value;
        { (inputval.length)  > 0 ?
            setShowResults(true)
            :
            setShowResults(false)
        }
        setFocusedOptionIndex(null);
        setValue(event.target.value);
        debouncedSearch(event.target.value);
    };

    const handleonfocus = (event: ChangeEvent<HTMLInputElement>) => {
        const focusval = event.target.value;
        { focusval ? setShowResults(true) : setShowResults(false) }
    };

    const handleOptionClick = useCallback(
        (option: Location) => {
            setValue(renderLocationLabel(option));
            setShowResults(false);
            setFocusedOptionIndex(null);
            onLocationSelect(option);
            setState({ suburb: option?.name + ' ' + option?.state + ' ' + option?.postcode, newsuburbname: option?.name });
        },
        [onLocationSelect, setState],
    );

    const renderResults = useCallback(() => {
        if (loading) {
            return <InfoMessage>Loading...</InfoMessage>;
        }
        if (error) {
            return <ErrorMessage>Error: {JSON.stringify(error)}</ErrorMessage>;
        }

        const safeData = data ?? [];

        if (safeData.length === 0) {
            return <InfoMessage>No suburb found. Please check spelling or search by postcode.</InfoMessage>;
        }

        return safeData.map((result, index) => (
            <ResultRow
                key={renderLocationLabel(result)}
                isFocused={index === focusedOptionIndex}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionClick(result);
                }}
                onMouseOver={() => {
                    setFocusedOptionIndex(index);
                }}
            >
                {renderLocationLabel(result)}
            </ResultRow>
        ));
    }, [data, error, focusedOptionIndex, handleOptionClick, loading]);

    return (
            <Outer ref={ref}>
                <SearchWrapper>
                    <SuburbInput
                        // name="postcode"
                        // id="postcode"
                        placeholder="Search by suburb or postcode"
                        value={ (state.suburb) ? state.suburb : value }
                        type="text"
                        onChange={handleInputChange}
                        onFocus={handleonfocus}
                        // onFocus={() => setShowResults(true)}
                        onKeyDown={event => {
                            const safeData = data ?? [];
                            if (safeData.length === 0) return;
                            switch (event.key) {
                                case "ArrowUp":
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setFocusedOptionIndex(prev => {
                                        switch (prev) {
                                            case null:
                                            case 0:
                                                return safeData.length - 1;
                                            default:
                                                return (prev ?? 1) - 1;
                                        }
                                    });
                                    break;
                                case "ArrowDown":
                                    event.preventDefault();
                                    event.stopPropagation();
                                    setFocusedOptionIndex(prev => {
                                        switch (prev) {
                                            case null:
                                            case safeData.length - 1:
                                                return 0;
                                            default:
                                                return (prev ?? 0) + 1;
                                        }
                                    });
                                    break;
                                case "Enter":
                                case " ": {
                                    if (focusedOptionIndex != null) {
                                        const option = safeData[focusedOptionIndex ?? 0];
                                        if (option) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleOptionClick(option);
                                        }
                                    }
                                    break;
                                }
                            }
                        }}
                    />
                    {showResults && (
                        <CloseButton
                        onClick={() => {
                                setState({ suburb: '', newsuburbname: '' });
                                setValue("");
                                setFocusedOptionIndex(null);
                                props.onLocationSelect(undefined);
                                setShowResults(false);
                            }}
                        >
                            <CloseOutlinedIcon />
                        </CloseButton>
                    )}
                </SearchWrapper>
                {showResults && <Results className="region_results" ref={resultsScrollingRef}>{renderResults()}</Results>}                
            </Outer>
    );

};